import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  addDays,
  format,
  setMinutes,
  setHours,
  isAfter,
  getDay,
  startOfDay,
} from "date-fns";
import {
  book_appointment_client,
  get_appointment_by_doctor_id,
} from "../store/reducers/appointmentReducerClient";
import Loader from "../components/Loader";

const CustomerCalendar = (doctor) => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { doctorAppt } = useSelector((state) => state.appointmentsClient || []);
  const doctorData = doctor.doctor;
  const paymentMethods = doctorData.paymentMethods;
  const appointmentDuration = doctorData.businessInfo?.appointmentDuration;
  const doctorId = doctorData._id;
  const userId = userInfo.id;
  const [isLoading, setIsLoading] = useState(false);
  const today = startOfDay(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [appointmentSlots, setAppointmentSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const Endate = format(selectedDate, "dd-MM-yyyy");
  const EnDayName = format(selectedDate, "EEEE");
  const arabicDays = {
    Sunday: "الأحد",
    Monday: "الاثنين",
    Tuesday: "الثلاثاء",
    Wednesday: "الأربعاء",
    Thursday: "الخميس",
    Friday: "الجمعة",
    Saturday: "السبت",
  };

  const arabicDay = arabicDays[EnDayName];
  const [formData, setFormData] = useState({
    userId: userId,
    doctorId: doctorId,
    slotDate: "",
    slotTime: "",
    duration: appointmentDuration,
    userData: { name: "", email: "", phone: "", age: "", gender: "" },
    doctorData: {
      name: doctorData.name,
      email: doctorData.email,
      phone: doctorData.phone,
      speciality: doctorData.speciality,
      experience: doctorData.experience,
      businessName: doctorData.businessInfo?.businessName,
      wilaya: doctorData.businessInfo?.wilaya,
      commune: doctorData.businessInfo?.commune,
    },
    amount: doctorData.businessInfo?.price || 0,
    payment: false,
    payMethod: "",
    appointmentStatus: "pending",
    bookedBy: `Client ${userInfo.name}`,
  });

  useEffect(() => {
    dispatch(get_appointment_by_doctor_id(doctorId));
  }, [doctorId, dispatch]);
  //console.log(appointments);

  useEffect(() => {
    if (doctorAppt) {
      const mappedSlots = doctorAppt.map((appt) => ({
        date: format(new Date(appt.slotDate), "yyyy-MM-dd"),
        time: appt.slotTime,
      }));

      setBookedSlots(mappedSlots);
    }
  }, [doctorAppt]);

  useEffect(() => {
    generateTimeSlots(selectedDate);
  }, [selectedDate, bookedSlots]);

  const generateTimeSlots = (date) => {
    const dayName = format(date, "EEEE");
    const dayData = doctorData.days?.[dayName];
    if (!dayData || !dayData.active) {
      setAppointmentSlots([]);
      return;
    }

    const { start, end } = dayData.workingHours;
    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);

    const startTime = setHours(
      setMinutes(new Date(date), startMinute),
      startHour
    );
    const endTime = setHours(setMinutes(new Date(date), endMinute), endHour);
    const slots = [];

    let currentSlot = startTime;
    while (isAfter(endTime, currentSlot)) {
      slots.push(format(currentSlot, "HH:mm"));
      currentSlot = new Date(
        currentSlot.getTime() + appointmentDuration * 60000
      );
    }

    setAppointmentSlots(slots);
  };

  const getAvailableSlotsCount = (date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    const totalSlots = appointmentSlots.length;
    const bookedCount = bookedSlots.filter(
      (slot) => slot.date === formattedDate
    ).length;

    return totalSlots - bookedCount;
  };

  const isSlotBooked = (slot) => {
    const formattedSelectedDate = format(selectedDate, "yyyy-MM-dd");
    return bookedSlots.some(
      (booked) => booked.date === formattedSelectedDate && booked.time === slot
    );
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setFormData((prevData) => ({
      ...prevData,
      slotDate: format(selectedDate, "yyyy-MM-dd"),
      slotTime: slot,
    }));
    setShowBookingForm(true);
  };

  const handleInputChange = (e, section, field) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: e.target.value,
      },
    }));
  };

  const inputHandle = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]: value,
      };
      if (name === "payMethod" && value === "Payer maintenant - ادفع الآن") {
        setShowPaymentForm(true);
      } else if (name === "payMethod") {
        setShowPaymentForm(false);
      }
      return updatedData;
    });
  };

  const handleFormSubmit = async (e) => {
    setIsLoading(true);
    const { name, email, phone, age, gender } = formData.userData;
    if (!name || !email || !phone || !age || !gender) {
      toast.error("Veuillez remplir tous les champs du formulaire.");
      toast.error("يرجى ملء جميع المدخلات في النموذج.");
      return;
    }
    e.preventDefault();

    await dispatch(book_appointment_client(formData))
      .then((res) => {
        if (res.payload) {
          setTimeout(() => {
            setIsLoading(false); // Hide loader after 3 seconds
          }, 3000);
        } else {
          setIsLoading(false); // Hide loader if an error occurs
        }
      })
      .catch((error) => {
        setIsLoading(false); // Ensure loader hides on error
        console.error("Échec de la réservation:", error);
      });
    dispatch(get_appointment_by_doctor_id(doctorId));
    setShowBookingForm(false);
    setFormData({
      userId: userId,
      doctorId: doctorId,
      slotDate: "",
      slotTime: "",
      duration: appointmentDuration,
      userData: { name: "", email: "", phone: "", age: "", gender: "" },
      doctorData: {
        name: doctorData.name,
        email: doctorData.email,
        phone: doctorData.phone,
        speciality: doctorData.speciality,
        experience: doctorData.experience,
        businessName: doctorData.businessInfo?.businessName,
      },
      amount: doctorData.businessInfo?.price || 0,
      payment: false,
      payMethod: "",
      appointmentStatus: "pending",
      bookedBy: `Client ${userInfo.name}`,
    });
    toast.success(
      "Vous avez réservé votre rendez-vous, vous recevrez une confirmation sur votre email."
    );
  };

  const handleBookingClose = () => {
    setShowBookingForm(false);
    setFormData((prevData) => ({
      ...prevData,
      payMethod: "",
    }));
  };
  const handlePayClose = () => {
    setShowPaymentForm(false);
    setFormData((prevData) => ({
      ...prevData,
      payMethod: "payLater",
    }));
  };

  return (
    <div className="doctor-calendar-container">
      {isLoading && <Loader />}
      <h2 className="lg:text-md text-lg mb-5 ">
        Calendrier de rendez-vous - تقويم المواعيد
      </h2>

      {/* Calendar Section for 60 Days */}
      <div className="calendar-section">
        {Array.from({ length: 60 }).map((_, index) => {
          const currentDate = addDays(today, index);
          const formattedDate = format(currentDate, "EEEE-dd-MMM");
          const formattedDate2 = format(currentDate, "yyyy-MM-dd");
          const dayname = format(currentDate, "EEEE");
          const availableSlots = getAvailableSlotsCount(formattedDate2);
          const dayData = doctorData.days?.[dayname];
          const ArabicDay = arabicDays[dayname];
          const slotText = availableSlots > 0 ? `Disponible` : "Complet";

          return (
            <div
              key={formattedDate}
              className={`calendar-day-box ${
                dayData?.active ? "active" : "inactive"
              } ${
                selectedDate.toDateString() === currentDate.toDateString()
                  ? "bg-mainColor"
                  : "bg-mainBg"
              }`}
              onClick={() => handleDayClick(currentDate)}
            >
              <h4>
                {formattedDate} - {ArabicDay}
              </h4>
              <p>{`${dayData?.active ? slotText : "Weekend"}`}</p>
            </div>
          );
        })}
      </div>

      {/* Appointment Slots Section */}
      <div className=" mt-5 bg-mainBg p-5 rounded-md lg:text-sm text-lg">
        <h3 className="flex mb-3">
          <span>Créneaux</span> -{" "}
          <span className="px-2 mx-2 bg-mainColor rounded-md border border-thirdColor">
            {Endate} - {arabicDay}
          </span>{" "}
          - <span>فتحات ليوم </span>{" "}
        </h3>
        <div className="flex flex-wrap">
          {appointmentSlots.length > 0 ? (
            appointmentSlots.map((slot, index) => (
              <Button
                key={index}
                className={`slot-box ${
                  isSlotBooked(slot)
                    ? "bg-red-500 cursor-not-allowed"
                    : "bg-mainColor hover:bg-[#5b0053]"
                }`}
                onClick={() => !isSlotBooked(slot) && handleSlotClick(slot)}
                disabled={isSlotBooked(slot)}
              >
                {slot}
              </Button>
            ))
          ) : (
            <p>
              No available slots for this day - لا توجد أماكن متاحة لهذا اليوم.
            </p>
          )}
        </div>
      </div>

      {/* Appointment Booking Form */}
      {showBookingForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-md rounded-xl">
          <div className="absolute bottom-[15%] bg-mainBg backdrop-blur-[6px] p-6 rounded-xl shadow-lg w-[40%] lg:w-[95%]">
            <h2 className="text-lg md:text-sm font-semibold mb-6 text-center">
              Confirmation pour le {Endate} - {formData.slotTime} {arabicDay}
            </h2>
            <Form onSubmit={handleFormSubmit}>
              <div className="flex flex-col w-full gap-1 mb-3 text-sm  mx-2">
                <div className="flex justify-between px-1">
                  <label htmlFor="name">Nom et prénom</label>
                  <label htmlFor="name">الاسم الكامل</label>
                </div>
                <input
                  value={formData.userData.name}
                  onChange={(e) => handleInputChange(e, "userData", "name")}
                  className="px-3 py-2 outline-none border border-slate-300 bg-transparent rounded-md focus:border-mainColor overflow-hidden"
                  type="text"
                  name="name"
                  placeholder="nom et prenom"
                  id="name"
                  required
                />
              </div>

              <div className="flex flex-col w-full gap-1 mb-3 text-sm mx-2">
                <div className="flex justify-between px-1">
                  <label htmlFor="email">Email</label>
                  <label htmlFor="email">بريد إلكتروني</label>
                </div>
                <input
                  value={formData.userData.email}
                  onChange={(e) => handleInputChange(e, "userData", "email")}
                  className="px-3 py-2 outline-none border border-slate-300 bg-transparent rounded-md focus:border-mainColor overflow-hidden"
                  type="email"
                  name="email"
                  placeholder="email du patient"
                  id="email"
                  required
                />
              </div>

              <div className="flex flex-col w-full gap-1 mb-3 text-sm mx-2">
                <div className="flex justify-between px-1">
                  <label htmlFor="phone">Téléphone</label>
                  <label htmlFor="phone"> رقم الهاتف</label>
                </div>
                <input
                  value={formData.userData.phone}
                  onChange={(e) => handleInputChange(e, "userData", "phone")}
                  className="px-3 py-2 outline-none border border-slate-300 bg-transparent rounded-md focus:border-mainColor overflow-hidden"
                  type="number"
                  name="phone"
                  placeholder="numero de telephone"
                  id="phone"
                  required
                />
              </div>
              <div className="flex flex-col w-full gap-1 mb-3 text-sm mx-2">
                <div className="flex justify-between px-1">
                  <label htmlFor="age">Age</label>
                  <label htmlFor="age">العمر</label>
                </div>
                <input
                  value={formData.userData.age}
                  onChange={(e) => handleInputChange(e, "userData", "age")}
                  className="px-3 py-2 outline-none border border-slate-300 bg-transparent rounded-md focus:border-mainColor overflow-hidden"
                  type="text"
                  name="age"
                  placeholder="votre age"
                  id="age"
                  required
                />
              </div>
              <div className="flex flex-col w-full gap-1 mb-3 text-sm mx-2">
                <div className="flex justify-between px-1">
                  <label htmlFor="gender">Gender</label>
                  <label htmlFor="gender"> الجنس</label>
                </div>
                <select
                  value={formData.userData.gender}
                  onChange={(e) => handleInputChange(e, "userData", "gender")}
                  className="px-3 py-2 outline-none border border-slate-300 bg-transparent rounded-md focus:border-mainColor overflow-hidden"
                  name="gender"
                  id="gender"
                  required
                >
                  <option value="">Sélectionnez - حدد الجنس</option>
                  <option value="Homme - ذكر">Homme - ذكر</option>
                  <option value="Femme - أنثى">Femme - أنثى</option>
                </select>
              </div>
              <div className="flex flex-col w-full gap-1 mb-3 text-sm mx-2">
                <div className="flex justify-between px-1">
                  <label htmlFor="payMethod">Mode de paiement</label>
                  <label htmlFor="payMethod"> طريقة الدفع</label>
                </div>
                <select
                  value={formData.payMethod}
                  onChange={inputHandle}
                  className="px-3 py-2 outline-none border border-slate-300 bg-transparent rounded-md focus:border-mainColor overflow-hidden"
                  name="payMethod"
                  id="payMethod"
                  required
                >
                  <option value="">Sélectionnez</option>
                  <option value="Payer maintenant - ادفع الآن">
                    Payer maintenant - ادفع الآن
                  </option>
                  <option value="Payer plus tard - ادفع لاحقا">
                    Payer plus tard - ادفع لاحقا
                  </option>
                </select>
              </div>

              {/* Additional fields for birthday, gender, doctor data, etc. */}
            </Form>
            <div className="flex justify-between gap-4 mt-5">
              <button
                onClick={() => handleBookingClose()}
                className="second-btn sm:text-sm"
              >
                Annuler - إلغاء
              </button>
              <button
                type="submit"
                onClick={handleFormSubmit}
                className="main-btn2 sm:text-sm"
              >
                Réservez - احجز الآن
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Appointment Booking Form */}
      {showPaymentForm && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black/20 z-50 backdrop-blur-md rounded-xl">
          <div className="absolute bottom-[20%] bg-mainBg backdrop-blur-[6px] p-6 rounded-xl shadow-lg w-[85%] lg:w-[95%] ">
            <h3 className="text-lg text-center mb-3 font-semibold">
              Méthodes de Paiement - طرق الدفع
            </h3>

            {paymentMethods && Object.keys(paymentMethods).length > 0 ? (
              <div>
                <p className="text-sm">
                  - Veuillez utiliser votre téléphone pour payer avec l'un des
                  modes de paiement ici, puis téléchargez une preuve de
                  paiement, une capture d'écran ou un PDF.
                </p>
                <p className="text-right text-sm mb-3">
                  يرجى استخدام هاتفك للدفع إلى إحدى طرق الدفع هنا، ثم قم بتحميل
                  لقطة شاشة أو ملف لإثبات الدفع-
                </p>
                <div className="flex flex-wrap">
                  {paymentMethods?.baridiMethod &&
                    Object.keys(paymentMethods.baridiMethod).length > 0 && (
                      <div
                        className="p-4 m-2 text-slate-300 w-[260px] rounded-lg shadow-slate-800/40 shadow-lg bg-cover bg-no-repeat"
                        style={{
                          backgroundImage: `url(${process.env.REACT_APP_CLIENT_URL}/images/payment/pay3.jpg)`,
                        }}
                      >
                        <div>
                          <h3>
                            {paymentMethods.baridiMethod?.baridiName ||
                              "لا يوجد تفاصيل، الرجاء اختيار طريقة دفع أخرى"}
                          </h3>
                          <h3>{paymentMethods.baridiMethod?.rip || ""}</h3>
                        </div>
                        <div className="flex justify-end">
                          <img
                            src={`${process.env.REACT_APP_CLIENT_URL}/images/payment/cib.png`}
                            alt="Payment Method"
                            className="w-[80px] bg-slate-300/10 backdrop-blur-md rounded-xl "
                          />
                        </div>
                      </div>
                    )}
                  {paymentMethods?.cardMethod &&
                    Object.keys(paymentMethods.cardMethod).length > 0 && (
                      <div
                        className="p-4 m-2 text-slate-200 w-[260px] rounded-lg shadow-slate-800/40 shadow-lg bg-cover bg-no-repeat"
                        style={{
                          backgroundImage: `url(${process.env.REACT_APP_CLIENT_URL}/images/payment/pay2.jpg)`,
                        }}
                      >
                        <div>
                          <h3>{paymentMethods.cardMethod.cardHolderName}</h3>
                          <h3>{paymentMethods.cardMethod.cardNumber}</h3>
                        </div>
                        <div className="flex justify-end items-center mt-2 ">
                          <img
                            src={
                              paymentMethods.cardMethod?.cardType === "Visa"
                                ? `${process.env.REACT_APP_CLIENT_URL}/images/payment/visa.png`
                                : `${process.env.REACT_APP_CLIENT_URL}/images/payment/master.png`
                            }
                            alt="Payment Method"
                            className="w-[80px] bg-slate-300/10 backdrop-blur-md rounded-xl mt-2"
                          />
                        </div>
                      </div>
                    )}

                  {paymentMethods?.bankTransfer &&
                    Object.keys(paymentMethods.bankTransfer).length > 0 && (
                      <div
                        className="p-4 m-2 text-slate-300 w-[260px] rounded-lg shadow-slate-800/40 shadow-lg bg-cover bg-no-repeat"
                        style={{
                          backgroundImage: `url(${process.env.REACT_APP_CLIENT_URL}/images/payment/pay5.jpg)`,
                        }}
                      >
                        <div className="font-bold text-sm line-clamp-5">
                          <h3>{paymentMethods.bankTransfer.bankName}</h3>
                          <h3>{paymentMethods.bankTransfer.accountHolder}</h3>
                          <h3>{paymentMethods.bankTransfer.iban}</h3>
                          <h3>{paymentMethods.bankTransfer.swift_bic}</h3>
                          <h3>{paymentMethods.bankTransfer.address}</h3>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center">
                <p className="text-center text-sm text-red-400">
                  Aucun moyen de paiement, veuillez payer en espèces
                </p>
                <p className="text-center text-sm text-red-400">
                  {" "}
                  لا توجد طرق دفع ، الرجاء الدفع نقدًا{" "}
                </p>
              </div>
            )}
            <div className="flex justify-between gap-4 mt-5">
              <button
                onClick={() => handlePayClose()}
                className="second-btn sm:text-sm"
              >
                Annuler - إلغاء
              </button>
              {paymentMethods && Object.keys(paymentMethods).length > 0 && (
                <button type="submit" className="main-btn2 sm:text-sm">
                  Payer - ادفع الآن
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerCalendar;
